import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EquipmentInventoryTable = ({ equipment, onCalibrate, onMaintenance, onViewDetails }) => {
  const [sortField, setSortField] = useState('nextDueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');

  const getStatusConfig = (status) => {
    switch (status) {
      case 'operational':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Operational'
        };
      case 'maintenance':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Clock',
          label: 'Maintenance'
        };
      case 'calibration_due':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'AlertTriangle',
          label: 'Calibration Due'
        };
      case 'out_of_service':
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'XCircle',
          label: 'Out of Service'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'Circle',
          label: 'Unknown'
        };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high':
        return { color: 'text-error', label: 'High' };
      case 'medium':
        return { color: 'text-warning', label: 'Medium' };
      case 'low':
        return { color: 'text-success', label: 'Low' };
      default:
        return { color: 'text-muted-foreground', label: 'Normal' };
    }
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const sortedAndFilteredEquipment = useMemo(() => {
    let filtered = equipment;

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(item => item?.status === filterStatus);
    }

    if (filterDepartment !== 'all') {
      filtered = filtered?.filter(item => item?.department === filterDepartment);
    }

    return filtered?.sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      if (sortField === 'nextDueDate' || sortField === 'lastMaintenance') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [equipment, sortField, sortDirection, filterStatus, filterDepartment]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const departments = [...new Set(equipment.map(item => item.department))];

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      {/* Header with Filters */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Equipment Inventory</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor calibration status and maintenance schedules
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            >
              <option value="all">All Status</option>
              <option value="operational">Operational</option>
              <option value="maintenance">Maintenance</option>
              <option value="calibration_due">Calibration Due</option>
              <option value="out_of_service">Out of Service</option>
            </select>
            
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            >
              <option value="all">All Departments</option>
              {departments?.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('deviceId')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors duration-150"
                >
                  <span>Equipment ID</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors duration-150"
                >
                  <span>Device Name</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Department</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('lastMaintenance')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors duration-150"
                >
                  <span>Last Maintenance</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('nextDueDate')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors duration-150"
                >
                  <span>Next Due</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Priority</th>
              <th className="text-right p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredEquipment?.map((item) => {
              const statusConfig = getStatusConfig(item?.status);
              const priorityConfig = getPriorityConfig(item?.priority);
              const daysUntilDue = getDaysUntilDue(item?.nextDueDate);
              
              return (
                <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors duration-150">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Settings" size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{item?.deviceId}</p>
                        <p className="text-xs text-muted-foreground">Serial: {item?.serialNumber}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-foreground">{item?.name}</p>
                      <p className="text-sm text-muted-foreground">{item?.model}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-foreground">{item?.department}</span>
                  </td>
                  <td className="p-4">
                    <div className={`inline-flex items-center space-x-2 px-2 py-1 rounded-full ${statusConfig?.bgColor}`}>
                      <Icon name={statusConfig?.icon} size={12} className={statusConfig?.color} />
                      <span className={`text-xs font-medium ${statusConfig?.color}`}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-foreground">
                      {new Date(item.lastMaintenance)?.toLocaleDateString('en-GB')}
                    </span>
                  </td>
                  <td className="p-4">
                    <div>
                      <span className="text-sm text-foreground">
                        {new Date(item.nextDueDate)?.toLocaleDateString('en-GB')}
                      </span>
                      <p className={`text-xs ${daysUntilDue <= 7 ? 'text-error' : daysUntilDue <= 30 ? 'text-warning' : 'text-muted-foreground'}`}>
                        {daysUntilDue > 0 ? `${daysUntilDue} days` : `${Math.abs(daysUntilDue)} days overdue`}
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`text-sm font-medium ${priorityConfig?.color}`}>
                      {priorityConfig?.label}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetails(item)}
                        iconName="Eye"
                        iconSize={14}
                      >
                        View
                      </Button>
                      {item?.status === 'calibration_due' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onCalibrate(item)}
                          iconName="Zap"
                          iconSize={14}
                        >
                          Calibrate
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onMaintenance(item)}
                        iconName="Wrench"
                        iconSize={14}
                      >
                        Maintain
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {sortedAndFilteredEquipment?.length} of {equipment?.length} equipment</span>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error/20 rounded-full border-2 border-error"></div>
              <span>Overdue</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning/20 rounded-full border-2 border-warning"></div>
              <span>Due Soon</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success/20 rounded-full border-2 border-success"></div>
              <span>Current</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentInventoryTable;